import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { GruppeTier } from '../classes/gruppe-tier';
import { Tierreklamation } from '../classes/tierreklamation';
import { TierreklamationCheckbox } from '../classes/tierreklamation-checkbox';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { GruppenService } from '../../gruppen/gruppen.service';
import { MessageService } from '../../../services/message-service/message.service';
import { StammdatenService } from '../../stammdaten/stammdaten.service';
import { DetachedRouteHandlesService } from '../../../configuration/route-caching-strategy/detached-route-handles.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { GruppeTierReklamationEditDialogComponent } from '../gruppe-tier-reklamation-neu-dialog/gruppe-tier-reklamation-edit-dialog.component';
import { GruppeTierService } from '../gruppe-tier.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';

@Component({
    selector: 'kiene-gruppe-tier-reklamieren',
    templateUrl: './gruppe-tier-reklamieren.component.html',
    styleUrls: ['./gruppe-tier-reklamieren.component.scss'],
})
export class GruppeTierReklamierenComponent implements OnInit, OnDestroy {
    @Input('dialog') dialog = false;
    @Input('tier') tier: GruppeTier;

    @Output('saved')
    saved: EventEmitter<boolean> = new EventEmitter<boolean>();

    isLoading = true;
    isSaving = false;
    tierId: number;
    ueberschrift = 'Tierreklamation hinzufügen';
    tierreklamationen: Tierreklamation[] = [];
    tierreklamationCheckboxes: TierreklamationCheckbox[] = [];
    canEditTierreklamationMaster = false;

    loadTierreklamationenSub: Subscription;
    gruppeTierSubscription: Subscription;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private gruppenService: GruppenService,
        private messageService: MessageService,
        private stammdatenService: StammdatenService,
        private detachedRouteHandlesService: DetachedRouteHandlesService,
        private matDialog: MatDialog,
        private gruppeTierService: GruppeTierService,
        private localStorageService: LocalStorageService,
    ) {}

    ngOnDestroy(): void {
        this.loadTierreklamationenSub?.unsubscribe();
        this.gruppeTierSubscription?.unsubscribe();
    }

    ngOnInit(): void {
        this.canEditTierreklamationMaster = this.currentUserCanEditTierreklamationMaster();
        this.tierId = this.tier ? this.tier.gt_id : this.route.snapshot.params['gt_id'];
        this.loadTier();

        this.detachedRouteHandlesService.watchChanges().subscribe({
            next: () => {
                this.loadTierreklamationen();
            },
        });
    }

    loadTierreklamationen(): void {
        this.isLoading = true;
        this.loadTierreklamationenSub?.unsubscribe();
        this.loadTierreklamationenSub = this.stammdatenService.getTierreklamationen(true).subscribe({
            next: tierreklamationen => {
                this.isLoading = false;
                this.tierreklamationen = [];
                this.tierreklamationCheckboxes = [];
                this.tierreklamationen = tierreklamationen.records;
                this.ensureTierReklamationenArray();
                this.tierreklamationen.forEach((tierreklamation: Tierreklamation) => {
                    const reklamationFound = this.hasReklamationId(tierreklamation.tierreklamation_id);
                    this.tierreklamationCheckboxes.push(
                        new TierreklamationCheckbox(tierreklamation, reklamationFound),
                    );
                });
                this.syncTierReklamationenFromCheckboxes();
            },
            error: err => {
                this.isLoading = false;
                this.messageService.errorMessage('Fehler beim Laden der Tierreklamationen', err);
            },
        });
    }

    loadTier() {
        this.gruppeTierSubscription?.unsubscribe();
        this.isLoading = true;
        this.gruppeTierSubscription = this.gruppenService.getGruppeTier(this.tierId).subscribe({
            next: tier => {
                this.tier = tier;
                this.ensureTierReklamationenArray();
                this.loadTierreklamationen();
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden des Tiers', err);
                this.isLoading = false;
            },
        });
    }

    editTierreklamation(event: MouseEvent, tierreklamation?: Tierreklamation): void {
        event.stopPropagation();
        if (!this.canEditTierreklamationMaster) {
            return;
        }

        this.matDialog
            .open(GruppeTierReklamationEditDialogComponent, {
                width: '90vw',
                minWidth: '90vw',
                data: { tierreklamation: tierreklamation },
            })
            .afterClosed()
            .subscribe((result: Tierreklamation) => {
                if (result) {
                    const existing = this.tierreklamationCheckboxes.find(
                        cb => Number(cb.tierreklamation.tierreklamation_id) === Number(result.tierreklamation_id),
                    );
                    if (existing) {
                        existing.checked = true;
                    } else {
                        this.tierreklamationCheckboxes.push(new TierreklamationCheckbox(result, true));
                    }
                    if (!this.hasReklamationId(result.tierreklamation_id)) {
                        this.tier.reklamationen.push(result);
                    }
                }
            });
    }

    reklamationHinzufuegen() {
        if (!this.tier?.gt_id) {
            return;
        }
        this.isSaving = true;
        const reklamationIds = Array.from(
            new Set(
                this.tierreklamationCheckboxes
                    .filter(cb => cb.checked)
                    .map(cb => Number(cb.tierreklamation.tierreklamation_id))
                    .filter(id => id > 0),
            ),
        );

        this.gruppeTierService.updateReklamationenMultiple([this.tier], reklamationIds, this.tier.gruppe_id).subscribe({
            next: success => {
                this.isSaving = false;
                if (!success) {
                    this.messageService.errorMessage('Reklamationen konnten nicht gespeichert werden!', null);
                    return;
                }
                if (this.dialog) {
                    this.syncTierReklamationenFromCheckboxes();
                    this.saved.emit(true);
                } else {
                    this.messageService.infoMessage('Die Reklamation wurde hinzugefügt');
                    this.router.navigate(['../../'], { relativeTo: this.route }).then();
                }
            },
            error: err => {
                this.isSaving = false;
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    addSelected(
        change: MatCheckboxChange | { checked },
        tierreklamationCheckbox: TierreklamationCheckbox,
        click?: MouseEvent,
    ) {
        if (click) {
            click.preventDefault();
            click.stopPropagation();
        }
        if (change.checked) {
            tierreklamationCheckbox.checked = true;
            if (!this.hasReklamationId(tierreklamationCheckbox.tierreklamation.tierreklamation_id)) {
                this.tier.reklamationen.push(tierreklamationCheckbox.tierreklamation);
            }
        } else {
            tierreklamationCheckbox.checked = false;
            const id = Number(tierreklamationCheckbox.tierreklamation.tierreklamation_id);
            this.tier.reklamationen = (this.tier.reklamationen ?? []).filter(
                x => Number(x.tierreklamation_id) !== id,
            );
        }
    }

    private ensureTierReklamationenArray(): void {
        if (!Array.isArray(this.tier?.reklamationen)) {
            this.tier.reklamationen = [];
        }
    }

    private hasReklamationId(tierreklamationId: number | string): boolean {
        const id = Number(tierreklamationId);
        if (!id) {
            return false;
        }
        return (this.tier?.reklamationen ?? []).some(x => Number(x.tierreklamation_id) === id);
    }

    private syncTierReklamationenFromCheckboxes(): void {
        this.tier.reklamationen = this.tierreklamationCheckboxes
            .filter(cb => cb.checked)
            .map(cb => cb.tierreklamation);
    }

    private currentUserCanEditTierreklamationMaster(): boolean {
        return this.localStorageService.hasPermission(9232);
    }
}
